import { useDeferredValue, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
    PiArrowLeftBold,
    PiArrowSquareOutBold,
    PiBoatBold,
    PiClockCounterClockwise,
    PiCoinsBold,
    PiFileTextBold,
    PiIdentificationCardBold,
    PiMagnifyingGlassBold,
    PiPackageBold,
    PiShieldWarningBold,
    PiSignInBold,
    PiSparkleBold,
    PiUserGearBold,
} from 'react-icons/pi';
import { PageHeader, Skeleton } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

type Tab = 'did' | 'account';

type Change = {
    field: string;
    label: string;
    old: string | null;
    new: string | null;
    added?: string[];
    removed?: string[];
};

type Kind = 'created' | 'updated' | 'deleted' | 'info' | null;

type Item = {
    id: string;
    at: string;
    date: string;
    time: string;
    human: string;
    category: string;
    title: string;
    detail: string | null;
    link: string | null;
    count: number;
    alert: boolean;
    kind: Kind;
    changes: Change[];
    by?: string;
};

type ApiResponse = {
    user: {
        id: number;
        name: string;
        email: string;
        avatar_url: string | null;
        is_active: boolean;
        roles: string[];
        created_at: string | null;
    };
    categories: Record<string, string>;
    summary: {
        by_category: Record<string, number>;
        last_login: string | null;
        logins: number;
        failed_logins: number;
    };
    data: Item[];
    total: number;
};

const FALLBACK = '/img/favicon.png';

const PERIODS = [
    { days: 7, label: '7 dias' },
    { days: 30, label: '30 dias' },
    { days: 90, label: '90 dias' },
    { days: 180, label: '6 meses' },
    { days: 365, label: '1 ano' },
];

const CATEGORY_STYLE: Record<string, { icon: ReactNode; tone: string }> = {
    access: { icon: <PiSignInBold />, tone: 'bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-300' },
    shipments: { icon: <PiBoatBold />, tone: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300' },
    stock: { icon: <PiPackageBold />, tone: 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300' },
    commission: { icon: <PiCoinsBold />, tone: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300' },
    documents: { icon: <PiFileTextBold />, tone: 'bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-300' },
    registry: { icon: <PiUserGearBold />, tone: 'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300' },
    ai: { icon: <PiSparkleBold />, tone: 'bg-pink-50 text-pink-600 dark:bg-pink-950/40 dark:text-pink-300' },
};

function dayHeading(date: string): string {
    const d = new Date(date + 'T12:00:00');
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
    if (same(d, today)) return 'Hoje';
    if (same(d, yesterday)) return 'Ontem';
    return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

const KIND_BADGE: Record<Exclude<Kind, null | 'info'>, { label: string; className: string }> = {
    created: { label: 'criou', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900' },
    updated: { label: 'alterou', className: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900' },
    deleted: { label: 'apagou', className: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900' },
};

function DiffLine({ sign, children }: { sign: '+' | '-'; children: ReactNode }) {
    const add = sign === '+';
    return (
        <div
            className={cn(
                'flex gap-2 px-3 py-0.5',
                add
                    ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'
                    : 'bg-rose-50 text-rose-900 dark:bg-rose-950/30 dark:text-rose-200',
            )}
        >
            <span className={cn('w-3 shrink-0 select-none font-bold', add ? 'text-emerald-600' : 'text-rose-600')}>{add ? '+' : '−'}</span>
            <span className={cn('min-w-0 break-words', !add && 'line-through decoration-rose-300/70')}>{children}</span>
        </div>
    );
}

const DIFF_PREVIEW = 4;

function DiffBlock({ changes, kind }: { changes: Change[]; kind: Kind }) {
    const [open, setOpen] = useState(changes.length <= DIFF_PREVIEW);
    const visible = open ? changes : changes.slice(0, DIFF_PREVIEW);

    return (
        <div className="mt-1.5 overflow-hidden rounded-md border border-gray-200 font-mono text-[12px] leading-5 dark:border-gray-700">
            {visible.map((c) => (
                <div key={c.field} className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/70">
                    {kind === 'info' ? (
                        <div className="flex gap-2 px-3 py-0.5 text-gray-700 dark:text-gray-300">
                            <span className="shrink-0 font-sans font-semibold text-gray-500">{c.label}:</span>
                            <span className="min-w-0 break-words">{c.new ?? c.old}</span>
                        </div>
                    ) : (
                        <>
                            <div className="bg-gray-50 px-3 py-0.5 font-sans text-[11px] font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                                {c.label}
                            </div>
                            {c.added || c.removed ? (
                                <>
                                    {(c.removed ?? []).map((v) => (
                                        <DiffLine key={'-' + v} sign="-">{v}</DiffLine>
                                    ))}
                                    {(c.added ?? []).map((v) => (
                                        <DiffLine key={'+' + v} sign="+">{v}</DiffLine>
                                    ))}
                                    {!c.added?.length && !c.removed?.length && (
                                        <div className="px-3 py-0.5 text-gray-500">só mudou a ordem</div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {c.old !== null && <DiffLine sign="-">{c.old}</DiffLine>}
                                    {c.new !== null && <DiffLine sign="+">{c.new}</DiffLine>}
                                </>
                            )}
                        </>
                    )}
                </div>
            ))}
            {changes.length > DIFF_PREVIEW && (
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="block w-full bg-gray-50 px-3 py-1 text-left font-sans text-[11px] font-semibold text-primary hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                    {open ? 'Mostrar menos' : `Mostrar mais ${changes.length - DIFF_PREVIEW} campo(s)`}
                </button>
            )}
        </div>
    );
}

function Stat({ label, value, hint, icon, tone }: { label: string; value: ReactNode; hint?: string; icon: ReactNode; tone: string }) {
    return (
        <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg text-lg', tone)}>{icon}</span>
            <span className="min-w-0">
                <span className="block truncate text-lg font-bold tabular-nums leading-none text-gray-900 dark:text-gray-100">{value}</span>
                <span className="mt-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">{label}</span>
                {hint && <span className="block text-[11px] text-gray-400">{hint}</span>}
            </span>
        </div>
    );
}

export default function UserActivityLogPage() {
    const { id } = useParams<{ id: string }>();
    const [tab, setTab] = useState<Tab>('did');
    const [days, setDays] = useState(30);
    const [category, setCategory] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);

    const { data, isLoading, isFetching, isError } = useQuery<ApiResponse>({
        queryKey: ['user-activity-timeline', id, tab, days, category, deferredSearch],
        queryFn: () =>
            api
                .get(`/users/${id}/activity-log`, {
                    params: { tab, days, category: category ?? undefined, search: deferredSearch || undefined },
                })
                .then((r) => r.data),
        placeholderData: keepPreviousData,
    });

    const user = data?.user;
    const summary = data?.summary;
    const items = data?.data ?? [];

    const groups = useMemo(() => {
        const map = new Map<string, Item[]>();
        for (const item of items) {
            const list = map.get(item.date) ?? [];
            list.push(item);
            map.set(item.date, list);
        }
        return [...map.entries()];
    }, [items]);

    const actionsCount = useMemo(
        () =>
            Object.entries(summary?.by_category ?? {})
                .filter(([k]) => k !== 'access')
                .reduce((sum, [, n]) => sum + n, 0),
        [summary],
    );

    const changeTab = (next: Tab) => {
        setTab(next);
        setCategory(null);
    };

    return (
        <div className="@container">
            <PageHeader
                title={user ? `Atividade — ${user.name}` : 'Atividade do usuário'}
                breadcrumb={[
                    { name: 'Dashboard', href: '/' },
                    { name: 'Administração' },
                    { name: 'Usuários', href: '/users' },
                    ...(user ? [{ name: user.name, href: `/users/${id}` }] : []),
                    { name: 'Atividade' },
                ]}
            >
                <Link
                    to={`/users/${id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                    <PiArrowLeftBold className="size-4" />
                    Voltar
                </Link>
            </PageHeader>

            {isLoading ? (
                <div className="space-y-4">
                    <Skeleton className="h-20 w-full rounded-2xl" />
                    <div className="grid grid-cols-2 gap-3 @3xl:grid-cols-4">
                        {[0, 1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-20 rounded-xl" />
                        ))}
                    </div>
                    <Skeleton className="h-64 w-full rounded-2xl" />
                </div>
            ) : isError || !user || !summary ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 py-12 text-center text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-300">
                    Não foi possível carregar a atividade deste usuário.
                </div>
            ) : (
                <>
                    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4 dark:border-gray-700 dark:bg-gray-800/60">
                        <img
                            src={user.avatar_url || FALLBACK}
                            alt=""
                            className="size-11 rounded-full object-cover"
                            onError={(e) => ((e.target as HTMLImageElement).src = FALLBACK)}
                        />
                        <div className="min-w-0">
                            <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {user.name}
                                <span
                                    className={cn(
                                        'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                                        user.is_active
                                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                                            : 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
                                    )}
                                >
                                    {user.is_active ? 'Ativo' : 'Desativado'}
                                </span>
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {user.email}
                                {user.created_at && ` · no Hub desde ${user.created_at}`}
                            </p>
                        </div>
                        {user.roles.length === 0 && (
                            <span className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                                <PiShieldWarningBold className="size-4" />
                                Sem perfil de acesso: não vê nenhuma tela além do Início
                            </span>
                        )}
                    </div>

                    <div className="mb-5 grid grid-cols-2 gap-3 @3xl:grid-cols-4">
                        <Stat
                            label="Último acesso"
                            value={summary.last_login ?? 'sem registro'}
                            hint="Hub ou app"
                            icon={<PiSignInBold />}
                            tone={CATEGORY_STYLE.access.tone}
                        />
                        <Stat
                            label="Entradas no período"
                            value={summary.logins}
                            hint={`últimos ${days} dias`}
                            icon={<PiIdentificationCardBold />}
                            tone="bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300"
                        />
                        <Stat
                            label="Ações no período"
                            value={tab === 'did' ? actionsCount : '—'}
                            hint="embarques, estoque, comissão…"
                            icon={<PiClockCounterClockwise />}
                            tone="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300"
                        />
                        <Stat
                            label="Senha errada"
                            value={summary.failed_logins}
                            hint="tentativas de entrar"
                            icon={<PiShieldWarningBold />}
                            tone={
                                summary.failed_logins > 0
                                    ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-300'
                            }
                        />
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/60">
                        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 pt-3 dark:border-gray-700">
                            {(
                                [
                                    ['did', 'O que fez'],
                                    ['account', 'Alterações na conta'],
                                ] as [Tab, string][]
                            ).map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => changeTab(key)}
                                    className={cn(
                                        '-mb-px border-b-2 px-1 pb-2.5 text-sm font-semibold transition',
                                        tab === key
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
                                    )}
                                >
                                    {label}
                                </button>
                            ))}
                            <div className="ml-auto flex flex-wrap items-center gap-2 pb-2">
                                <label className="relative">
                                    <PiMagnifyingGlassBold className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Buscar na atividade"
                                        className="h-9 w-56 rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm outline-none focus:border-primary dark:border-gray-600 dark:bg-gray-900"
                                    />
                                </label>
                                <select
                                    value={days}
                                    onChange={(e) => setDays(Number(e.target.value))}
                                    aria-label="Período"
                                    className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                                >
                                    {PERIODS.map((p) => (
                                        <option key={p.days} value={p.days}>
                                            Últimos {p.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <p className="px-4 pt-3 text-xs text-gray-500 dark:text-gray-400">
                            {tab === 'did'
                                ? 'Tudo o que a pessoa fez no Hub e no app: entradas, tarefas de embarque, reservas e movimentações de estoque, comissão, documentos e uso da IA.'
                                : 'O que aconteceu com a conta: criação, mudança de dados e perfis, desativação, sincronização com o Ploomes e tentativas de entrar com senha errada.'}
                        </p>

                        <div className="flex flex-wrap gap-2 px-4 py-3">
                            <button
                                type="button"
                                onClick={() => setCategory(null)}
                                className={cn(
                                    'rounded-full border px-3 py-1 text-xs font-semibold transition',
                                    category === null
                                        ? 'border-primary bg-primary text-white'
                                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700',
                                )}
                            >
                                Tudo
                            </button>
                            {Object.entries(data.categories)
                                .filter(([key]) => (summary.by_category[key] ?? 0) > 0 || category === key)
                                .map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setCategory(category === key ? null : key)}
                                        className={cn(
                                            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition',
                                            category === key
                                                ? 'border-primary bg-primary text-white'
                                                : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700',
                                        )}
                                    >
                                        {CATEGORY_STYLE[key]?.icon}
                                        {label}
                                        <span className="tabular-nums opacity-70">{summary.by_category[key] ?? 0}</span>
                                    </button>
                                ))}
                        </div>

                        <div className={cn('px-4 pb-5 transition-opacity', isFetching && 'opacity-60')}>
                            {groups.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-gray-200 py-14 text-center dark:border-gray-700">
                                    <PiClockCounterClockwise className="mx-auto mb-3 size-10 text-gray-300 dark:text-gray-600" />
                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Nada registrado neste período</p>
                                    <p className="mt-1 text-xs text-gray-400">
                                        {search
                                            ? 'Tente outra busca ou limpe os filtros.'
                                            : `Aumente o período${user.roles.length === 0 ? ' ou dê um perfil de acesso para a pessoa usar o Hub' : ''}.`}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {groups.map(([date, list]) => (
                                        <section key={date}>
                                            <h3 className="sticky top-0 z-[1] mb-2 bg-white py-1 text-xs font-semibold uppercase tracking-wide text-gray-400 first-letter:uppercase dark:bg-gray-800">
                                                {dayHeading(date)}
                                            </h3>
                                            <ol className="relative space-y-1 border-l border-gray-200 pl-5 dark:border-gray-700">
                                                {list.map((item) => {
                                                    const style = CATEGORY_STYLE[item.category] ?? CATEGORY_STYLE.registry;
                                                    return (
                                                        <li key={item.id} className="relative">
                                                            <span
                                                                className={cn(
                                                                    'absolute -left-[33px] top-2 flex size-6 items-center justify-center rounded-full text-xs ring-4 ring-white dark:ring-gray-800',
                                                                    item.alert
                                                                        ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300'
                                                                        : style.tone,
                                                                )}
                                                            >
                                                                {item.alert ? <PiShieldWarningBold /> : style.icon}
                                                            </span>
                                                            <div className="group flex items-start gap-3 rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                                                <span className="w-11 shrink-0 pt-0.5 text-xs tabular-nums text-gray-400" title={item.human}>
                                                                    {item.time}
                                                                </span>
                                                                <div className="min-w-0 flex-1">
                                                                    <p
                                                                        className={cn(
                                                                            'text-sm font-medium',
                                                                            item.alert ? 'text-rose-700 dark:text-rose-300' : 'text-gray-900 dark:text-gray-100',
                                                                        )}
                                                                    >
                                                                        {item.kind && item.kind !== 'info' && (
                                                                            <span
                                                                                className={cn(
                                                                                    'mr-2 rounded px-1.5 py-0.5 align-[1px] text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset',
                                                                                    KIND_BADGE[item.kind].className,
                                                                                )}
                                                                            >
                                                                                {KIND_BADGE[item.kind].label}
                                                                            </span>
                                                                        )}
                                                                        {item.title}
                                                                        {item.kind === 'updated' && item.changes.length > 0 && (
                                                                            <span className="ml-2 text-[11px] font-normal text-gray-400">
                                                                                {item.changes.length} campo{item.changes.length > 1 ? 's' : ''}
                                                                            </span>
                                                                        )}
                                                                        {item.count > 1 && (
                                                                            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                                                                ×{item.count}
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                    {(item.detail || item.by) && (
                                                                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                                                            {item.by && <span className="font-medium">por {item.by}</span>}
                                                                            {item.by && item.detail && ' · '}
                                                                            {item.detail}
                                                                        </p>
                                                                    )}
                                                                    {item.changes.length > 0 && <DiffBlock changes={item.changes} kind={item.kind} />}
                                                                    {item.count > 1 && item.changes.length > 0 && (
                                                                        <p className="mt-1 text-[11px] text-gray-400">
                                                                            A mesma alteração em {item.count} registros.
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                {item.link && (
                                                                    <Link
                                                                        to={item.link}
                                                                        className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary opacity-70 hover:bg-primary/10 group-hover:opacity-100"
                                                                    >
                                                                        Abrir <PiArrowSquareOutBold className="size-3.5" />
                                                                    </Link>
                                                                )}
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ol>
                                        </section>
                                    ))}
                                    {data.total > items.length && (
                                        <p className="text-center text-xs text-gray-400">
                                            Mostrando as {items.length} mais recentes de {data.total}. Diminua o período ou filtre por área.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
